class LaureaConfigurator extends HTMLElement {
  constructor() {
    super();
    this.formId = this.dataset.productFormId;
    this.productRoot = this.closest('.product') || document;

    this.charactersContainer = this.querySelector('[data-laurea-characters-container]');
    this.characterTemplate = this.querySelector('[data-laurea-character-template]');
    this.countSelect = this.querySelector('[data-laurea-character-count]');
    this.personaggiInclusi = parseInt(this.dataset.personaggiInclusi, 10) || 1;

    this.animalCountSelect = this.querySelector('[data-laurea-animal-count]');
    this.animalsContainer = this.querySelector('[data-laurea-animals-container]');

    this.palloncinoToggle = this.querySelector('[data-laurea-palloncino-toggle]');
    this.palloncinoFields = this.querySelector('[data-laurea-palloncino-fields]');

    if (this.countSelect) {
      this.countSelect.addEventListener('change', this.onCountChange.bind(this));
    }
    if (this.animalCountSelect) {
      this.animalCountSelect.addEventListener('change', this.onAnimalCountChange.bind(this));
    }
    if (this.palloncinoToggle) {
      this.palloncinoToggle.addEventListener('change', this.applyPalloncinoToggle.bind(this));
    }
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    }

    this.renderCharacters(this.currentCharacterCount());
    this.renderAnimals(this.currentAnimalCount());
    this.applyPalloncinoToggle();
    this.updateNamesVisibility();
    this.addOptionHints();
  }

  // With a single character the count select isn't rendered at all.
  currentCharacterCount() {
    return this.countSelect ? parseInt(this.countSelect.value, 10) || 1 : 1;
  }

  currentAnimalCount() {
    return this.animalCountSelect ? parseInt(this.animalCountSelect.value, 10) || 0 : 0;
  }

  onCountChange(event) {
    this.renderCharacters(parseInt(event.target.value, 10) || 1);
    this.updateNamesVisibility();
  }

  onAnimalCountChange(event) {
    this.renderAnimals(parseInt(event.target.value, 10) || 0);
    this.recomputeCostiExtra();
  }

  onVariantChange() {
    this.updateNamesVisibility();
    this.addOptionHints();
  }

  setGroupDisabled(group, disabled) {
    if (!group) return;
    group.hidden = disabled;
    group.querySelectorAll('input, select, textarea').forEach((el) => {
      el.disabled = disabled;
    });
  }

  applyPalloncinoToggle() {
    if (!this.palloncinoToggle) return;
    this.setGroupDisabled(this.palloncinoFields, this.palloncinoToggle.value !== 'Sì');
  }

  getNamesOptionInputs() {
    return this.productRoot.querySelectorAll('input[data-option-name="Nomi sopra le teste"]');
  }

  updateNamesVisibility() {
    if (!this.charactersContainer) return;
    const checked = Array.from(this.getNamesOptionInputs()).find((input) => input.checked);
    const showNames = checked ? checked.value.startsWith('Sì') : false;
    this.charactersContainer.querySelectorAll('[data-laurea-nome-field]').forEach((field) => {
      field.hidden = !showNames;
      field.querySelectorAll('input').forEach((el) => {
        el.disabled = !showNames;
      });
    });
  }

  addOptionHints() {
    const hints = {
      'Nomi sopra le teste': '(+5€)',
      'Cornice': '(legno +5€)',
    };
    Object.entries(hints).forEach(([optionName, hintText]) => {
      const input = this.productRoot.querySelector(`input[data-option-name="${optionName}"]`);
      const fieldset = input ? input.closest('fieldset') : null;
      const legend = fieldset ? fieldset.querySelector('legend') : null;
      if (!legend || legend.querySelector('.laurea-configurator__hint')) return;
      const span = document.createElement('span');
      span.className = 'laurea-configurator__hint';
      span.textContent = ' ' + hintText;
      legend.appendChild(span);
    });
  }

  renderCharacters(count) {
    if (!this.charactersContainer || !this.characterTemplate) return;

    this.charactersContainer.innerHTML = '';
    for (let i = 1; i <= count; i++) {
      this.charactersContainer.appendChild(this.buildCharacterFieldset(i));
    }

    this.updateNamesVisibility();
    this.recomputeCostiExtra();
  }

  // One "Cane / Gatto" picker per animal, so the order says which is which.
  renderAnimals(count) {
    if (!this.animalsContainer) return;
    this.animalsContainer.innerHTML = '';

    for (let i = 1; i <= count; i++) {
      const row = document.createElement('div');
      row.className = 'laurea-configurator__select-row';

      const label = document.createElement('label');
      label.className = 'form__label';
      label.textContent = count === 1 ? 'Tipo animaletto' : `Animaletto ${i}`;

      const wrap = document.createElement('div');
      wrap.className = 'select';

      const select = document.createElement('select');
      select.className = 'select__select';
      select.setAttribute('form', this.formId);
      select.setAttribute('name', count === 1 ? 'properties[Animaletto - Tipo]' : `properties[Animaletto ${i} - Tipo]`);
      ['Cane', 'Gatto'].forEach((v) => {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = v;
        select.appendChild(o);
      });

      wrap.appendChild(select);
      row.appendChild(label);
      row.appendChild(wrap);
      this.animalsContainer.appendChild(row);
    }
  }

  buildCharacterFieldset(index) {
    const html = this.characterTemplate.innerHTML
      .replaceAll('__INDEX__', index)
      .replaceAll('__FORM_ID__', this.formId);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    const fieldset = wrapper.firstElementChild;

    const fieldNames = {
      nome: `properties[Nome personaggio ${index}]`,
      sesso: `properties[Personaggio ${index} - Sesso]`,
      stoffa_colore: `properties[Personaggio ${index} - Colore stoffa]`,
      stoffa_numero: `properties[Personaggio ${index} - Fantasia stoffa]`,
      gadget: `properties[Personaggio ${index} - Gadget]`,
      pantaloni_o_jeans: `properties[Personaggio ${index} - Pantaloni o jeans]`,
      colore_pantaloni: `properties[Personaggio ${index} - Colore pantaloni]`,
      colore_jeans: `properties[Personaggio ${index} - Colore jeans]`,
      papillon_si_no: `properties[Personaggio ${index} - Papillon]`,
      papillon_tipo: `properties[Personaggio ${index} - Tipo papillon]`,
      papillon_preferenze: `properties[Personaggio ${index} - Preferenze papillon]`,
    };

    fieldset.querySelectorAll('[data-field]').forEach((el) => {
      const key = el.dataset.field;
      if (fieldNames[key]) el.setAttribute('name', fieldNames[key]);
      el.setAttribute('form', this.formId);
    });

    const sessoSelect = fieldset.querySelector('[data-field="sesso"]');
    const femminaFields = fieldset.querySelector('[data-laurea-femmina-fields]');
    const maschioFields = fieldset.querySelector('[data-laurea-maschio-fields]');
    const pantaloniToggle = fieldset.querySelector('[data-laurea-pantaloni-toggle]');
    const pantaloniColorRow = fieldset.querySelector('[data-laurea-pantaloni-color]');
    const jeansColorRow = fieldset.querySelector('[data-laurea-jeans-color]');
    const papillonToggle = fieldset.querySelector('[data-laurea-papillon-toggle]');
    const papillonFields = fieldset.querySelector('[data-laurea-papillon-fields]');
    const gadgetSelect = fieldset.querySelector('[data-laurea-gadget-select]');

    const applyPantaloniToggle = () => {
      if (!pantaloniToggle) return;
      this.setGroupDisabled(pantaloniColorRow, pantaloniToggle.value !== 'Pantaloni');
      this.setGroupDisabled(jeansColorRow, pantaloniToggle.value !== 'Jeans');
    };

    const applyPapillonToggle = () => {
      if (!papillonToggle) return;
      this.setGroupDisabled(papillonFields, papillonToggle.value !== 'Sì');
    };

    const applySesso = () => {
      const isFemmina = sessoSelect.value === 'Femmina';
      const isMaschio = sessoSelect.value === 'Maschio';
      this.setGroupDisabled(femminaFields, !isFemmina);
      this.setGroupDisabled(maschioFields, !isMaschio);
      if (isMaschio) {
        applyPantaloniToggle();
        applyPapillonToggle();
      }
      this.recomputeCostiExtra();
    };

    if (sessoSelect) sessoSelect.addEventListener('change', applySesso);
    if (pantaloniToggle) pantaloniToggle.addEventListener('change', applyPantaloniToggle);
    if (papillonToggle) papillonToggle.addEventListener('change', applyPapillonToggle);
    if (gadgetSelect) gadgetSelect.addEventListener('change', this.recomputeCostiExtra.bind(this));

    applySesso();

    return fieldset;
  }

  // The hidden "Costi extra" option carries the euro total of every automatic
  // surcharge: €5 per gadget, €5 per character beyond the ones included in the
  // base price, €3 per animal.
  recomputeCostiExtra() {
    if (!this.charactersContainer) return;

    let gadgets = 0;
    this.charactersContainer.querySelectorAll('[data-laurea-gadget-select]').forEach((select) => {
      if (!select.disabled && select.value !== 'Nessuno') gadgets++;
    });

    const extra = Math.max(0, this.currentCharacterCount() - this.personaggiInclusi);
    this.setCostiExtraVariant(gadgets * 5 + extra * 5 + this.currentAnimalCount() * 3);
  }

  setCostiExtraVariant(euro) {
    // Only the euro-denominated option is targeted: the former "Animaletti
    // extra" counter held a number of animals, so matching it here would
    // silently pick a wrong price while the product options are being migrated.
    const inputs = this.productRoot.querySelectorAll('input[data-option-name="Costi extra"]');
    if (!inputs.length) return;
    const target = Array.from(inputs).find((i) => (parseInt(i.value, 10) || 0) === euro);
    if (target && !target.checked) {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

customElements.define('laurea-configurator', LaureaConfigurator);
